/**
 * As três abas de Tempos. Moram fora da tela porque a barra de endereços também fala
 * delas: as rotas antigas (`/curadoria-tempos`, `/sequencias`, `/cronometragem`) mandam
 * para a aba certa, e um valor digitado à mão que não existe cai na aba padrão em vez de
 * deixar a tela sem painel nenhum.
 *
 * As três telas tratam do MESMO assunto, o tempo por peça, e a pessoa pula entre elas:
 * vê na Curadoria que um produto está estimado, vai medir em Cronometrar e volta às
 * Sequências para colocar em uso. Juntas viram um caminho só.
 */
import { CRONOMETRAGEM_PRODUTO_PARAM } from '@/app/cronometragem/helpers/parametrosDaCronometragem';
import { FICHA_DO_PRODUTO_PARAM } from '@/app/sequencias/helpers/fichaDoProduto';
import { ACTIVE_TAB_PARAM } from '@/domain/common/hooks/useActiveTabParam';

export const TEMPOS_PATH = '/tempos';

/** A Curadoria de tempos: o que cada produto tem hoje e o que falta medir. */
export const TAB_DIAGNOSTICO = 'diagnostico';
/** As sequências cadastradas, que é onde um tempo medido entra em uso. */
export const TAB_SEQUENCIAS = 'sequencias';
/** A Cronometragem: medir o produto com o cronômetro na mão. */
export const TAB_CRONOMETRAR = 'cronometrar';

/** A aba que abre quando o endereço não pede nenhuma: a curadoria do dia. */
export const TAB_PADRAO = TAB_DIAGNOSTICO;

export const TEMPOS_TAB_VALUES: ReadonlyArray<string> = [
  TAB_DIAGNOSTICO,
  TAB_SEQUENCIAS,
  TAB_CRONOMETRAR,
];

/**
 * A busca que a tabela da aba já abre preenchida. As três abas falam de PRODUTO, então
 * "Ver sequência" e "Ver na curadoria" levam o produto para a busca da outra: é o que
 * deixa as três telas virarem um caminho só em vez de três listas inteiras.
 */
export const BUSCA_PRODUTO_PARAM = 'buscaProduto';

/** O que um pulo entre abas escreve no endereço; `null` apaga a chave. */
export type TTemposPatch = Record<string, string | null>;

/**
 * Os PULOS entre as abas, num lugar só. Cada um diz três coisas: para qual aba ir, onde a
 * aba de destino lê o produto, e o que fica para trás.
 *
 * Moram aqui porque a regra é uma e os botões que a usam são seis, espalhados pelos três
 * painéis: seis cópias já tinham divergido entre si (umas fechavam a ficha aberta, outras
 * não), e é assim que "Ver sequência" passa a se comportar diferente de "Ver na
 * curadoria" sem ninguém decidir isso.
 *
 * A ficha do produto (`produto`) SEMPRE fica para trás: ela é uma gaveta aberta sobre a
 * aba de origem, e reabrir sozinha do outro lado é ruído. O produto em medição
 * (`cronometrarProduto`) NÃO é apagado ao sair de Cronometrar: ele é o caminho de volta
 * para a medição em andamento, e só a própria Cronometrar o lê.
 */
export function irParaDiagnostico(produto: string): TTemposPatch {
  return {
    [ACTIVE_TAB_PARAM]: TAB_DIAGNOSTICO,
    [BUSCA_PRODUTO_PARAM]: produto,
    [FICHA_DO_PRODUTO_PARAM]: null,
  };
}

export function irParaSequencias(produto: string): TTemposPatch {
  return {
    [ACTIVE_TAB_PARAM]: TAB_SEQUENCIAS,
    [BUSCA_PRODUTO_PARAM]: produto,
    [FICHA_DO_PRODUTO_PARAM]: null,
  };
}

export function irParaCronometrar(produto: string): TTemposPatch {
  return {
    [ACTIVE_TAB_PARAM]: TAB_CRONOMETRAR,
    [CRONOMETRAGEM_PRODUTO_PARAM]: produto,
    [FICHA_DO_PRODUTO_PARAM]: null,
    // A busca é das outras duas abas: deixá-la aqui faria o próximo pulo levar um
    // recorte velho para uma lista que não é a deste produto.
    [BUSCA_PRODUTO_PARAM]: null,
  };
}

/**
 * O endereço de uma aba, PRESERVANDO o recorte que já veio. É o que as rotas antigas
 * usam: um link antigo com filtro (`/sequencias?produto=X`) tem de chegar na aba certa
 * com o mesmo filtro, senão a redireção conserta o endereço e perde a tela.
 *
 * `extra` entra por último, então quem monta um link novo manda no valor: o botão
 * "Cronometrar" da Curadoria troca o produto mesmo que a barra já tivesse um. `null`
 * TIRA o parâmetro, que é como um link entre abas limpa o que era da aba de origem (a
 * ficha aberta não deve reabrir sozinha do outro lado).
 */
export function temposHref(
  aba: string,
  recorteAtual?: URLSearchParams | string | null,
  extra?: Readonly<Record<string, string | null>>,
): string {
  const params = new URLSearchParams(recorteAtual ?? undefined);
  for (const [chave, valor] of Object.entries(extra ?? {})) {
    if (valor === null) {
      params.delete(chave);
      continue;
    }
    params.set(chave, valor);
  }
  params.set(ACTIVE_TAB_PARAM, aba);
  return `${TEMPOS_PATH}?${params.toString()}`;
}
